import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import {
  fetchCentre,
  fetchPlannerGradeGroup,
  fetchPlannerPlanDetail,
  fetchPlannerPlans
} from 'api'
import { showToast } from 'app/helpers'
import { downloadXlsxSheets } from 'app/pages/Procurement/components/exportUtils'

import PlanEditor from './PlanEditor'

const items = (d) =>
  d && Array.isArray(d.results) ? d.results : Array.isArray(d) ? d : []

const addDays = (iso, n) => {
  const d = new Date(iso)
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

// Monday (YYYY-MM-DD) of the week containing `input`.
const mondayOf = (input) => {
  const date = new Date(input)
  if (isNaN(date.getTime())) return ''
  const day = (date.getDay() + 6) % 7
  date.setDate(date.getDate() - day)
  return date.toISOString().slice(0, 10)
}

// weekday is stored 0=Mon..4=Fri (planner runs Mon–Fri); the plan's
// week_start_date is that Monday, so a cell's date = Monday + weekday.
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

const arr = (v) => (Array.isArray(v) ? v : [])

// UTC-safe (the value is a plain date, no time zone should shift it).
const addDaysUtc = (iso, n) => {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

// "31 Aug 2026" from a YYYY-MM-DD.
const prettyDate = (iso) => {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso || ''
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${dd} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

// Materials are a list of (often multi-line) strings on each cell/item; one
// activity's whole materials list becomes one Excel cell.
const materialsText = (m) =>
  arr(m)
    .map((s) => (s == null ? '' : String(s)).trim())
    .filter(Boolean)
    .join('\n')

/**
 * Teacher → Lesson Planner. The weekly lesson-plan builder, one card per class —
 * a distinct teacher set at a (centre, grade), with its day-length batches folded
 * in. The class's teacher(s) claim a timetable, then fill the grid. A teacher
 * login is pinned by the backend to their own class(es); admins see all.
 */
// ?centre=<uuid>&week= opens this page on that centre (Quality and Ops
// teacher rows link here, Satya 29 Oct).
const linkParam = (name) => {
  try {
    return new URLSearchParams(window.location.search).get(name) || ''
  } catch (e) {
    return ''
  }
}

const LessonPlanner = () => {
  const [centres, setCentres] = useState([])
  const [grades, setGrades] = useState([])
  const [centreId, setCentreId] = useState(() => linkParam('centre'))
  const [gradeId, setGradeId] = useState('')
  const [weekStart, setWeekStart] = useState(
    () => mondayOf(linkParam('week') || new Date()) || mondayOf(new Date())
  )
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [section, setSection] = useState(null) // chosen class card

  useEffect(() => {
    fetchCentre({})
      .then((c) => setCentres(items(c)))
      .catch(() => showToast('Failed to load centres', 'danger'))
    fetchPlannerGradeGroup({})
      .then((g) => setGrades(items(g)))
      .catch(() => {})
  }, [])

  const load = async () => {
    if (!centreId) {
      setCards([])
      return
    }
    setLoading(true)
    try {
      const q = { centre: centreId, week_start: weekStart }
      if (gradeId) q.grade_group = gradeId
      const data = await fetchPlannerPlans({ queries: q })
      setCards((data && data.sections) || [])
    } catch (e) {
      showToast('Failed to load classes', 'danger')
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centreId, gradeId, weekStart])

  // ----- Download materials: every class at this centre for this week, one
  // row per activity, so a centre head sees the whole week's materials at once
  // (the app only shows them one day/activity at a time). -----
  const downloadMaterials = async () => {
    if (!centreId || !cards.length) return
    setDownloading(true)
    try {
      const centreName =
        (cards[0].section && cards[0].section.centre_name) || ''
      const details = await Promise.all(
        cards.map((card) =>
          fetchPlannerPlanDetail({
            queries: {
              centre: card.section.centre,
              grade: card.section.grade_group,
              class_key: card.section.class_key,
              week_start: weekStart
            }
          })
            .then((plan) => ({ card, plan }))
            .catch(() => ({ card, plan: null }))
        )
      )
      const weekLabel = `Week of ${prettyDate(weekStart)}`
      const items = []
      details.forEach(({ card, plan }) => {
        const s = card.section
        const cells = (plan && arr(plan.cells)) || []
        cells.forEach((cell) => {
          const isoDate = addDaysUtc(weekStart, cell.weekday || 0)
          const day = WEEKDAY_NAMES[cell.weekday] || ''
          // A multi-activity ("PICK") slot lists each of its activities on its
          // own row; a normal slot is one row from the cell itself.
          const units =
            cell.is_multi && arr(cell.items).length
              ? arr(cell.items).map((it) => ({
                  title: it.title || cell.title,
                  materials: it.materials
                }))
              : [{ title: cell.title, materials: cell.materials }]
          units.forEach((u) => {
            const activity = (u.title || '').trim()
            const materials = materialsText(u.materials)
            if (!activity && !materials) return // empty slot
            items.push({
              cls: s.grade_group_name || '',
              teacher: s.teacher_name || '',
              iso: isoDate,
              activity: activity || '(untitled)',
              cols: [
                s.centre_name || centreName,
                s.grade_group_name || '',
                s.teacher_name || '',
                weekLabel,
                prettyDate(isoDate),
                day,
                activity || '(untitled)',
                materials
              ]
            })
          })
        })
      })
      if (!items.length) {
        showToast('No planned activities to export for this week', 'info')
        return
      }
      // Read like a prep list: by class, then through the week, then activity.
      items.sort(
        (a, b) =>
          a.cls.localeCompare(b.cls) ||
          a.teacher.localeCompare(b.teacher) ||
          a.iso.localeCompare(b.iso) ||
          a.activity.localeCompare(b.activity)
      )
      const headers = [
        'Centre',
        'Class',
        'Teacher',
        'Week',
        'Date',
        'Day',
        'Activity',
        'Materials'
      ]
      const safe = `${centreName || 'centre'}_${weekStart}`.replace(
        /[^\w.-]+/g,
        '_'
      )
      downloadXlsxSheets(`Materials_${safe}.xlsx`, [
        { name: 'Materials', headers, rows: items.map((o) => o.cols) }
      ])
    } catch (e) {
      showToast('Failed to build the materials file', 'danger')
    } finally {
      setDownloading(false)
    }
  }

  // ----- editor for a chosen class -----
  if (section) {
    const s = section.section
    return (
      <Box p={2}>
        <Button size="small" onClick={() => setSection(null)}>
          ◀ Back to classes
        </Button>
        <Typography variant="h5" style={{ marginTop: 8 }}>
          {s.grade_group_name}
          {s.teacher_name ? ` · ${s.teacher_name}` : ''}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {s.centre_name} · week of {weekStart}
        </Typography>
        <PlanEditor
          centre={s.centre}
          grade={s.grade_group}
          classKey={s.class_key}
          weekStart={weekStart}
          onClose={() => setSection(null)}
        />
      </Box>
    )
  }

  // ----- class picker -----
  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Lesson Planner
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Build a class&apos;s weekly lesson plan — the same planner teachers use
        in their app.
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mt={2}
        mb={2}
      >
        <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
          <InputLabel>Centre</InputLabel>
          <Select
            label="Centre"
            value={centreId}
            onChange={(e) => setCentreId(e.target.value)}
          >
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Grade</InputLabel>
          <Select
            label="Grade"
            value={gradeId}
            onChange={(e) => setGradeId(e.target.value)}
          >
            <MenuItem value="">
              <em>All grades</em>
            </MenuItem>
            {grades.map((g) => (
              <MenuItem key={g.uuid} value={g.uuid}>
                {g.emoji ? `${g.emoji} ` : ''}
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          onClick={() => setWeekStart(addDays(weekStart, -7))}
        >
          ◀ Prev
        </Button>
        <TextField
          type="date"
          size="small"
          label="Week of"
          InputLabelProps={{ shrink: true }}
          value={weekStart}
          onChange={(e) => {
            const m = mondayOf(e.target.value)
            if (m) setWeekStart(m)
          }}
        />
        <Button
          size="small"
          onClick={() => setWeekStart(addDays(weekStart, 7))}
        >
          Next ▶
        </Button>
        <Box flexGrow={1} />
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!centreId || downloading || !cards.length}
          onClick={downloadMaterials}
        >
          {downloading ? 'Preparing…' : '⬇ Download materials'}
        </Button>
      </Box>

      {!centreId ? (
        <Typography variant="body2" color="textSecondary">
          Pick a centre to see its classes.
        </Typography>
      ) : loading ? (
        <CircularProgress />
      ) : (
        <Box display="flex" flexWrap="wrap" style={{ gap: 14 }}>
          {cards.map((card) => {
            const s = card.section
            const required = card.required_count || 0
            const done = required > 0 && card.planned_count >= required
            return (
              <Paper
                key={s.uuid}
                variant="outlined"
                style={{
                  width: 264,
                  cursor: 'pointer',
                  borderRadius: 12,
                  padding: 14
                }}
                onClick={() => setSection(card)}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box>
                    <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                      {s.grade_group_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {s.teacher_name ? `${s.teacher_name} · ` : ''}
                      {s.centre_name}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={card.status === 'submitted' ? 'Submitted' : 'Draft'}
                    style={
                      card.status === 'submitted'
                        ? {
                            backgroundColor: '#2d9d63',
                            color: '#fff',
                            fontWeight: 600
                          }
                        : {
                            backgroundColor: '#efe9df',
                            color: '#7a6f65',
                            fontWeight: 600
                          }
                    }
                  />
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    s.needs_claim
                      ? 'Timetable not set'
                      : s.timetable_name || 'Timetable'
                  }
                  style={{
                    marginTop: 8,
                    color: s.needs_claim ? '#a15c00' : '#3a322b',
                    borderColor: s.needs_claim ? '#e0a75e' : '#d5ccc0'
                  }}
                />
                <Typography
                  variant="caption"
                  style={{
                    display: 'block',
                    marginTop: 8,
                    fontWeight: 700,
                    color: done ? '#2d9d63' : '#3a322b'
                  }}
                >
                  {card.planned_count}/{required} sessions planned
                </Typography>
              </Paper>
            )
          })}
          {!cards.length && (
            <Typography variant="body2" color="textSecondary">
              No classes set up for this centre yet.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default LessonPlanner
